import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users,
  Clock,
  FolderKanban,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Globe,
  BarChart3,
  BarChart2,
  Sparkles,
  Trophy,
  AlertCircle,
  LineChart,
  CalendarClock,
  MapPin,
  Building2,
  CalendarRange,
  Layers,
  MessageCircle,
  X,
  CreditCard,
} from 'lucide-react';
import { useUserSession } from '../../context/UserSessionContext';
import { useAuth } from '../../context/AuthContext';
import { useSyncData } from '../../context/SyncContext';
import { UserMenu } from './user-menu';
import { appPath } from '../../utils/appPath';
import { createMCPClientURL, refreshTokenIfNeeded } from '../../utils/tokenUtils';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
  badge?: string;
  children?: Omit<NavItem, 'children'>[];
}

const NAV_GROUPS: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      {
        label: 'Client Analytics',
        icon: <Users size={18} />,
        path: appPath('/client-management'),
      },
      {
        label: 'Currency Dashboard',
        icon: <Globe size={18} />,
        path: appPath('/currency-dashboard'),
      },
      {
        label: 'Account Owner Portfolio',
        icon: <BarChart2 size={18} />,
        path: appPath('/account-owner-portfolio'),
      },
      {
        label: 'Client Acquisition',
        icon: <Sparkles size={18} />,
        path: appPath('/client-acquisition-retention'),
      },
    ],
  },
  {
    title: 'Reports',
    items: [
      {
        label: 'Regional Performance',
        icon: <MapPin size={18} />,
        path: appPath('/regional-client'),
      },
      {
        label: 'Billing Entity Analysis',
        icon: <Building2 size={18} />,
        path: appPath('/entity-performance'),
      },
      {
        label: 'Collections Status',
        icon: <AlertCircle size={18} />,
        path: appPath('/outstanding-collections'),
      },
      {
        label: 'Overdue Tracking',
        icon: <CalendarClock size={18} />,
        path: appPath('/overdue-invoice-aging'),
      },
    ],
  },
  {
    title: 'Timesheet & RMG',
    items: [
      {
        label: 'Timesheet Insights',
        icon: <Layers size={18} />,
        path: appPath('/timesheet-insights'),
      },
      {
        label: 'Employee Analysis',
        icon: <Clock size={18} />,
        path: appPath('/employee-analysis'),
      },
      {
        label: 'Project Analysis',
        icon: <FolderKanban size={18} />,
        path: appPath('/project-analysis'),
      },
    ],
  },
  {
    title: 'Insights & Trends',
    items: [
      {
        label: 'Top Clients',
        icon: <Trophy size={18} />,
        path: appPath('/top-clients-revenue'),
      },
      {
        label: 'Revenue Trends',
        icon: <LineChart size={18} />,
        path: appPath('/revenue-trends'),
      },
      {
        label: 'Seasonal Patterns',
        icon: <CalendarRange size={18} />,
        path: appPath('/revenue-seasonality'),
      },
    ],
  },
  {
    title: 'Finance',
    items: [
      {
        label: 'Subscriptions',
        icon: <CreditCard size={18} />,
        path: appPath('/subscription-management'),
      },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState<string[]>([]);
  const { features } = useUserSession();
  const { currentUser } = useAuth();
  const { formattedSyncDate } = useSyncData();
  const [showIframe, setShowIframe] = useState(false);
  const [mcpClientURL, setMcpClientURL] = useState('');
  const [iframeError, setIframeError] = useState(false);

  const navGroups = useMemo(() => {
    if (features.showBetaNav) return NAV_GROUPS;
    return NAV_GROUPS.map(g => ({
      ...g,
      items: g.items.filter(item => !item.badge),
    })).filter(g => g.items.length > 0);
  }, [features.showBetaNav]);

  const isActive = (path?: string) => {
    if (!path) return false;
    const basePath = path.split('?')[0];
    return location.pathname === basePath || location.pathname.startsWith(basePath + '/');
  };

  const toggleExpand = (label: string) => {
    setExpanded(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  const handleNav = (path?: string) => {
    if (path) navigate(path);
  };

  const handleConnectToAI = async () => {
    if (!currentUser) return;
    try {
      const token = await refreshTokenIfNeeded(currentUser);
      if (!token) {
        console.error('❌ Failed to generate SSO token');
        return;
      }
      const urlWithToken = createMCPClientURL(token);
      setMcpClientURL(urlWithToken);
      setIframeError(false);
      setShowIframe(true);
    } catch (error) {
      console.error('❌ Error connecting to AI chatbot:', error);
    }
  };

  const handleIframeError = () => {
    setShowIframe(false);
    setIframeError(true);
    const popup = window.open(mcpClientURL, 'mcpclient', 'width=1200,height=800,scrollbars=yes,resizable=yes,status=yes,location=yes,menubar=yes,toolbar=yes');
    if (!popup) alert('Both iframe and popup failed! Please allow popups for this site and try again.');
  };

  useEffect(() => {
    if (showIframe) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [showIframe]);

  return (
    <aside
      className={`
        fixed top-0 left-0 h-full z-40
        bg-slate-900 border-r border-slate-700/60
        flex flex-col
        transition-all duration-200 ease-in-out
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Brand */}
      <div className={`
        flex items-center h-[56px] px-4 border-b border-slate-700/60
        ${collapsed ? 'justify-center' : 'justify-between'}
      `}>
        {!collapsed && (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                src="/systango_logo.jpg"
                alt="Pulse"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white leading-tight truncate">Pulse</p>
              <p className="text-[11px] text-white/55 leading-tight">Financial Analytics</p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-7 h-7 rounded-lg flex items-center justify-center overflow-hidden">
            <img
              src="/systango_logo.jpg"
              alt="Pulse"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {!collapsed && (
          <button
            onClick={onToggle}
            className="w-6 h-6 rounded-md flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <ChevronLeft size={14} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {navGroups.map((group, gi) => (
          <div key={gi}>
            {group.title && !collapsed && (
              <p className="px-2 mb-1 text-[10px] font-semibold tracking-widest uppercase text-white/45">
                {group.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map(item => {
                const active = isActive(item.path) ||
                  (item.children?.some(c => isActive(c.path)) ?? false);
                const isOpen = expanded.includes(item.label);

                return (
                  <li key={item.label}>
                    <button
                      onClick={() =>
                        item.children ? toggleExpand(item.label) : handleNav(item.path)
                      }
                      title={collapsed ? item.label : undefined}
                      className={`
                        w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left
                        transition-colors duration-100 group relative
                        ${active
                          ? 'bg-white/15 text-white'
                          : 'text-white hover:bg-white/10 hover:text-white'
                        }
                        ${collapsed ? 'justify-center' : ''}
                      `}
                    >
                      {/* Active left bar */}
                      {active && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-blue-400 rounded-r-full" />
                      )}

                      <span className="flex-shrink-0 text-white">
                        {item.icon}
                      </span>

                      {!collapsed && (
                        <>
                          <span className="flex-1 text-sm font-medium truncate">
                            {item.label}
                          </span>
                          {item.badge && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                              {item.badge}
                            </span>
                          )}
                          {item.children && (
                            <ChevronDown
                              size={14}
                              className={`text-white/70 transition-transform duration-150 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                            />
                          )}
                        </>
                      )}
                    </button>

                    {/* Children */}
                    {item.children && !collapsed && isOpen && (
                      <ul className="mt-0.5 ml-4 pl-3 border-l border-slate-700 space-y-0.5">
                        {item.children.map(child => (
                          <li key={child.label}>
                            <button
                              onClick={() => handleNav(child.path)}
                              className={`
                                w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left
                                transition-colors duration-100
                                ${isActive(child.path)
                                  ? 'text-white bg-white/15'
                                  : 'text-white hover:text-white hover:bg-white/10'
                                }
                              `}
                            >
                              <span className="text-white">
                                {child.icon}
                              </span>
                              <span className="text-[13px] font-medium truncate">{child.label}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapsed toggle */}
      {collapsed && (
        <div className="px-2 py-3 border-t border-slate-700/60">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center py-2 rounded-lg text-white hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Sync date — show last data sync */}
      {!collapsed && (
        <div className="px-2 py-2 border-t border-slate-700/60">
          <p className="text-[10px] text-white/50 text-center">
            Last sync: {formattedSyncDate}
          </p>
        </div>
      )}

      {/* AI Chatbot button */}
      {!collapsed && (
        <div className="px-2 py-2 border-t border-slate-700/60">
          <button
            onClick={handleConnectToAI}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors text-sm font-medium"
          >
            <MessageCircle size={14} />
            AI Chatbot
          </button>
        </div>
      )}

      {/* User menu — relative so the dropdown anchors to the rail */}
      <div className="relative px-2 py-3 border-t border-slate-700/60">
        <UserMenu collapsed={collapsed} />
      </div>

      {/* AI Chatbot Modal */}
      {showIframe && (
        <div className="fixed inset-0 z-[100] flex flex-col h-screen bg-slate-900">
          <div className="flex items-center justify-between p-4 border-b border-slate-700/60">
            <h2 className="text-white font-semibold">AI Chatbot Assistant</h2>
            <button
              onClick={() => setShowIframe(false)}
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <iframe
            src={mcpClientURL}
            className="flex-1 w-full border-none"
            onError={handleIframeError}
          />
        </div>
      )}

      {/* Iframe error fallback notification */}
      {iframeError && (
        <div className="fixed bottom-4 right-4 bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm max-w-xs z-50">
          Failed to load chatbot. Try opening in popup instead.
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
